import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useEffect, useMemo, useState } from "react";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { InternBrandBuildsQuery } from "./__generated__/InternBrandBuildsQuery.graphql";
import { InternBrandBuilds_brand$key } from "./__generated__/InternBrandBuilds_brand.graphql";
import nullthrows from "../../utils/nullthrows";
import InternBuildActionsPanel from "./builds/InternBuildActionsPanel";
import InternBuildConfigPanel from "./builds/InternBuildConfigPanel";
import InternBuildHistorySection from "./builds/InternBuildHistorySection";
import InternBuildPipelinesPanel, {
  PipelineRow,
} from "./builds/InternBuildPipelinesPanel";
import InternBuildStatusSection from "./builds/InternBuildStatusSection";
import InternReadyToSubmitCard from "./builds/InternReadyToSubmitCard";
import InternReleaseNotesPreview from "./builds/InternReleaseNotesPreview";
import { getActionablePipelines } from "./builds/submitState";

const brandFragment = graphql`
  fragment InternBrandBuilds_brand on BrandType {
    id
    appConfig {
      appName
      iosAppVersion
      iosAppBuild
      androidAppVersion
      androidAppBuild
      ...InternBuildConfigPanel_appConfig
    }
  }
`;

const buildsDataQuery = graphql`
  query InternBrandBuildsQuery($brandId: ID!) {
    pendingIosScheduledBuild: pendingScheduledBuild(
      brandId: $brandId
      platform: "ios"
    ) {
      id
      platform
      changesCount
      scheduledFor
      lastChangeAt
      status
      pendingChanges
    }
    pendingAndroidScheduledBuild: pendingScheduledBuild(
      brandId: $brandId
      platform: "android"
    ) {
      id
      platform
      changesCount
      scheduledFor
      lastChangeAt
      status
      pendingChanges
    }
    scheduledBuilds(brandId: $brandId) {
      id
      platform
      changesCount
      scheduledFor
      lastChangeAt
      firstChangeAt
      status
      pendingChanges
      pipelineId
    }
    latestIosReview: latestAppStoreReview(brandId: $brandId, platform: "ios") {
      id
      platform
      appVersion
      buildNumber
      status
      statusDisplay
      submittedAt
      reviewedAt
      rejectionReason
    }
    latestAndroidReview: latestAppStoreReview(
      brandId: $brandId
      platform: "android"
    ) {
      id
      platform
      appVersion
      buildNumber
      status
      statusDisplay
      submittedAt
      reviewedAt
      rejectionReason
    }
    appStoreReviews(brandId: $brandId, limit: 10) {
      id
      platform
      appVersion
      buildNumber
      status
      statusDisplay
      submittedAt
      reviewedAt
      rejectionReason
    }
    appBuildPipelines(brandId: $brandId, limit: 15) {
      id
      platform
      status
      isAutomatic
      appVersion
      appBuildNumber
      startedAt
      completedAt
      circleciPipelineId
      submitOutcome
      submitOutcomeDetail
      includeReleaseNotes
      triggerReason
    }
    resolvedReleaseNotes(brandId: $brandId) {
      includeReleaseNotes
      notes {
        locale
        text
        isOverride
      }
    }
  }
`;

interface Props {
  brand: InternBrandBuilds_brand$key;
}

const SectionLabel = ({ children }: { children: string }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      mb: 1,
      color: "var(--intern-text-tertiary)",
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

const VersionPill = ({
  platform,
  version,
  build,
}: {
  platform: "ios" | "android";
  version?: string | null;
  build?: string | null;
}) => {
  const isIOS = platform === "ios";
  const Icon = isIOS ? AppleIcon : AndroidIcon;
  const hasVersion = Boolean(version && version !== "0");

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        bgcolor: "var(--intern-bg-secondary)",
        border: "1px solid var(--intern-border-default)",
      }}
    >
      <Icon
        sx={{
          fontSize: 16,
          color: isIOS ? "#007AFF" : "#3DDC84",
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: "var(--intern-text-tertiary)",
          fontSize: "0.6875rem",
          fontWeight: 600,
        }}
      >
        {isIOS ? "iOS" : "Android"}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "var(--intern-text-primary)",
          fontSize: "0.75rem",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {hasVersion ? `v${version}` : "—"}
        {hasVersion && build ? (
          <Box
            component="span"
            sx={{ color: "var(--intern-text-tertiary)", fontWeight: 500 }}
          >
            {" "}
            ({build})
          </Box>
        ) : null}
      </Typography>
    </Stack>
  );
};

const InternBrandBuilds = ({ brand: brandKey }: Props) => {
  const brand = useFragment(brandFragment, brandKey);
  const appConfig = nullthrows(brand.appConfig);
  const [fetchKey, setFetchKey] = useState(0);

  const buildsData = useLazyLoadQuery<InternBrandBuildsQuery>(
    buildsDataQuery,
    { brandId: brand.id },
    { fetchPolicy: "store-and-network", fetchKey }
  );

  const pipelines: PipelineRow[] = useMemo(
    () =>
      (buildsData.appBuildPipelines ?? []).map((p) => ({
        id: p.id,
        platform: p.platform,
        status: p.status,
        isAutomatic: p.isAutomatic,
        appVersion: p.appVersion,
        appBuildNumber: p.appBuildNumber,
        startedAt: p.startedAt,
        completedAt: p.completedAt ?? null,
        circleciPipelineId: p.circleciPipelineId,
        submitOutcome: p.submitOutcome,
        submitOutcomeDetail: p.submitOutcomeDetail,
        includeReleaseNotes: p.includeReleaseNotes,
        triggerReason: p.triggerReason,
      })),
    [buildsData.appBuildPipelines]
  );

  const hasInFlight = pipelines.some(
    (p) => p.status === "pending" || p.status === "running"
  );
  const actionablePipelines = useMemo(
    () => getActionablePipelines(pipelines),
    [pipelines]
  );

  const refetchBuilds = () => setFetchKey((k) => k + 1);

  useEffect(() => {
    if (!hasInFlight) return;
    const id = window.setInterval(() => {
      setFetchKey((k) => k + 1);
    }, 15000);
    return () => window.clearInterval(id);
  }, [hasInFlight]);

  const mapPendingSchedule = (
    node:
      | {
          id: string;
          platform: string;
          changesCount: number;
          scheduledFor: string;
          lastChangeAt: string;
          status: string;
          pendingChanges: unknown;
        }
      | null
      | undefined
  ) =>
    node
      ? {
          id: node.id,
          platform: node.platform,
          changesCount: node.changesCount,
          scheduledFor: node.scheduledFor,
          lastChangeAt: node.lastChangeAt,
          status: node.status,
          pendingChanges:
            (node.pendingChanges as Array<{
              field: string;
              new_value?: string;
              changed_at: string;
            }>) ?? [],
        }
      : null;

  const pendingScheduledBuilds = [
    mapPendingSchedule(buildsData.pendingIosScheduledBuild),
    mapPendingSchedule(buildsData.pendingAndroidScheduledBuild),
  ].filter((s): s is NonNullable<typeof s> => s != null);

  const scheduledBuildsHistory = (buildsData.scheduledBuilds ?? []).map(
    (b) => ({
      id: b.id,
      platform: b.platform,
      changesCount: b.changesCount,
      scheduledFor: b.scheduledFor,
      lastChangeAt: b.lastChangeAt,
      firstChangeAt: b.firstChangeAt,
      status: b.status,
      pipelineId: b.pipelineId ?? null,
      pendingChanges:
        (b.pendingChanges as Array<{
          field: string;
          new_value?: string;
          changed_at: string;
          requires_build?: boolean;
        }>) ?? [],
    })
  );

  const mapReview = (
    node:
      | {
          id: string;
          platform: string;
          appVersion: string;
          buildNumber: string;
          status: string;
          statusDisplay: string;
          submittedAt: string | null;
          reviewedAt: string | null;
        }
      | null
      | undefined
  ) =>
    node
      ? {
          id: node.id,
          platform: node.platform,
          appVersion: node.appVersion,
          buildNumber: node.buildNumber,
          status: node.status,
          statusDisplay: node.statusDisplay,
          submittedAt: node.submittedAt,
          reviewedAt: node.reviewedAt,
        }
      : null;

  const latestIosReview = mapReview(buildsData.latestIosReview);
  const latestAndroidReview = mapReview(buildsData.latestAndroidReview);

  const reviewHistory = (buildsData.appStoreReviews ?? []).map((r) => ({
    id: r.id,
    platform: r.platform,
    appVersion: r.appVersion,
    buildNumber: r.buildNumber,
    status: r.status,
    statusDisplay: r.statusDisplay,
    submittedAt: r.submittedAt,
    reviewedAt: r.reviewedAt,
    rejectionReason: r.rejectionReason,
  }));

  const releaseNotes = buildsData.resolvedReleaseNotes;

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      {/* Page header */}
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: { xs: 1.75, sm: 2 },
          bgcolor: "var(--intern-bg-primary)",
          border: "1px solid var(--intern-border-default)",
          borderRadius: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.75}>
            <Typography
              variant="h6"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.125rem" },
                lineHeight: 1.2,
              }}
            >
              Builds
            </Typography>
            {appConfig.appName ? (
              <Chip
                size="small"
                label={appConfig.appName}
                sx={{
                  height: 22,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  bgcolor: "var(--intern-bg-secondary)",
                  color: "var(--intern-text-secondary)",
                }}
              />
            ) : null}
            {hasInFlight ? (
              <Chip
                size="small"
                icon={
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: "0.625rem !important",
                      color: "#f59e0b !important",
                      animation: "pulse 1.4s ease-in-out infinite",
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.35 },
                      },
                    }}
                  />
                }
                label="Build in progress"
                sx={{
                  height: 22,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  bgcolor: "rgba(245, 158, 11, 0.12)",
                  color: "#b45309",
                }}
              />
            ) : null}
            {latestIosReview ? (
              <Chip
                size="small"
                label={latestIosReview.statusDisplay}
                sx={{
                  height: 22,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  bgcolor: "var(--intern-bg-secondary)",
                  color: "var(--intern-text-secondary)",
                }}
              />
            ) : null}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <VersionPill
              platform="ios"
              version={appConfig.iosAppVersion}
              build={appConfig.iosAppBuild}
            />
            <VersionPill
              platform="android"
              version={appConfig.androidAppVersion}
              build={appConfig.androidAppBuild}
            />
            {pendingScheduledBuilds.map((schedule) => (
              <Chip
                key={schedule.id}
                size="small"
                label={`${schedule.platform.toUpperCase()} scheduled · ${
                  schedule.changesCount
                } change${schedule.changesCount === 1 ? "" : "s"}`}
                sx={{
                  height: 30,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  bgcolor:
                    schedule.platform === "android"
                      ? "rgba(21, 128, 61, 0.1)"
                      : "rgba(59, 130, 246, 0.1)",
                  color:
                    schedule.platform === "android" ? "#15803d" : "#1d4ed8",
                  borderRadius: 1.5,
                }}
              />
            ))}
          </Stack>
        </Box>
      </Paper>

      {/* Actionable store review submit — one card per platform */}
      {actionablePipelines.length > 0 ? (
        <Stack spacing={2} sx={{ mb: 2.5 }}>
          {actionablePipelines.map((pipeline) => (
            <InternReadyToSubmitCard
              key={pipeline.id}
              brandId={brand.id}
              pipeline={pipeline}
              onSubmitted={refetchBuilds}
            />
          ))}
        </Stack>
      ) : null}

      {/* Manual push — separate from review submit */}
      <Box sx={{ mb: 2.5 }}>
        <SectionLabel>Push Build</SectionLabel>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 1.75 },
            bgcolor: "var(--intern-bg-primary)",
            border: "1px solid var(--intern-border-default)",
            borderRadius: 2,
          }}
        >
          <InternBuildActionsPanel
            brandId={brand.id}
            onKickedOff={refetchBuilds}
          />
        </Paper>
      </Box>

      {/* Pipelines history */}
      <Box sx={{ mb: 2.5 }}>
        <SectionLabel>Activity</SectionLabel>
        <InternBuildPipelinesPanel
          brandId={brand.id}
          pipelines={pipelines}
          onSubmitted={refetchBuilds}
        />
      </Box>

      {/* Secondary: schedule + review (single split surface) */}
      <Box sx={{ mb: 2.5 }}>
        <InternBuildStatusSection
          brandId={brand.id}
          scheduledBuilds={pendingScheduledBuilds}
          iosReview={latestIosReview}
          androidReview={latestAndroidReview}
          onRefresh={refetchBuilds}
        />
      </Box>

      {/* History (single split surface, equal scroll panes) */}
      <Box sx={{ mb: 2.5 }}>
        <InternBuildHistorySection
          builds={scheduledBuildsHistory}
          reviews={reviewHistory}
        />
      </Box>

      {/* Details */}
      <Box>
        <SectionLabel>Details</SectionLabel>
        <Stack spacing={1.5}>
          <InternReleaseNotesPreview
            includeReleaseNotes={releaseNotes?.includeReleaseNotes ?? false}
            notes={(releaseNotes?.notes ?? []).map((n) => ({
              locale: n.locale,
              text: n.text,
              isOverride: n.isOverride,
            }))}
          />
          <InternBuildConfigPanel appConfig={appConfig} />
        </Stack>
      </Box>
    </Box>
  );
};

export default InternBrandBuilds;
